<template>
    <div v-show="visible" class="box-model" @click.stop>
        <div class="box-model__margin" :style="marginStyles" />
        <div class="box-model__padding" :style="paddingStyles" />

        <VDropdown
            :offset="popupOffset"
            :open="visible"
            placement="top"
            :auto-hide="false"
            class="box-model__popover"
        >
            <template #popper>
                <box-model-legend
                    :margin="margin"
                    :padding="padding"
                    :offset="offset"
                />
            </template>
        </VDropdown>
    </div>
</template>

<script>
import { mapState } from 'vuex';

import BoxModelLegend from '@/components/shared/box-model/box-model-legend';
import { getTransformValues } from '@/utils/css';

export default {
    name: 'box-model',
    components: { BoxModelLegend },

    props: {
        visible: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            border: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
            },

            margin: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
            },

            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
            },

            offset: {
                x: 0,
                y: 0,
            },
        };
    },

    computed: {
        ...mapState({
            changedOption: state => state.page.changedOption,
            deviceMode: state => state.page.deviceMode,
        }),

        marginStyles() {
            return {
                'border-top-width': `${ this.margin.top }px`,
                'border-right-width': `${ this.margin.right }px`,
                'border-bottom-width': `${ this.margin.bottom }px`,
                'border-left-width': `${ this.margin.left }px`,

                top: `-${ this.margin.top + this.border.top }px`,
                right: `-${ this.margin.right + this.border.right }px`,
                bottom: `-${ this.margin.bottom + this.border.bottom }px`,
                left: `-${ this.margin.left + this.border.left }px`,
            };
        },

        paddingStyles() {
            return {
                'border-top-width': `${ this.padding.top }px`,
                'border-right-width': `${ this.padding.right }px`,
                'border-bottom-width': `${ this.padding.bottom }px`,
                'border-left-width': `${ this.padding.left }px`,
            };
        },

        popupOffset() {
            return this.border.top + (this.margin.top / 2);
        },
    },

    watch: {
        deviceMode() {
            this.setAll();
        },

        changedOption({ option }) {
            if (!this.visible) return;

            switch (option) {
                case 'border':
                    this.setBorder();
                    break;

                // the boxed option removes padding and margin when turned on
                case 'boxed':
                    this.setPadding();
                    this.setMargin();
                    break;

                case 'margin':
                    this.setMargin();
                    break;

                case 'padding':
                    this.setPadding();
                    break;

                case 'offset':
                    this.setOffset();
                    break;

                default:
                    // do nothing
            }
        },

        visible(isVisible) {
            if (!isVisible) return;

            this.setAll();
        },
    },

    methods: {
        async setAll() {
            await this.$nextTick();
            this.setBorder();
            this.setMargin();
            this.setPadding();
            this.setOffset();
        },

        async setBorder() {
            await this.$nextTick();
            this.border.top = this.getComputedStyle('border-top-width');
            this.border.right = this.getComputedStyle('border-right-width');
            this.border.bottom = this.getComputedStyle('border-bottom-width');
            this.border.left = this.getComputedStyle('border-left-width');
        },

        async setMargin() {
            await this.$nextTick();
            this.margin.top = this.getComputedStyle('margin-top');
            this.margin.right = this.getComputedStyle('margin-right');
            this.margin.bottom = this.getComputedStyle('margin-bottom');
            this.margin.left = this.getComputedStyle('margin-left');
        },

        async setPadding() {
            await this.$nextTick();
            this.padding.top = this.getComputedStyle('padding-top');
            this.padding.right = this.getComputedStyle('padding-right');
            this.padding.bottom = this.getComputedStyle('padding-bottom');
            this.padding.left = this.getComputedStyle('padding-left');
        },

        async setOffset() {
            await this.$nextTick();
            const transformObj = getTransformValues(this.$parent.$el.style.transform);

            if (!transformObj) return;

            this.offset.x = transformObj.x;
            this.offset.y = transformObj.y;
        },

        getComputedStyle(prop) {
            // n.b. this returns the value as a number, without the unit, e.g. 10 instead of "10px"
            return +window.getComputedStyle(this.$parent.$el).getPropertyValue(prop).slice(0, -2);
        },
    },
};
</script>

<style lang="scss">
.box-model {
    @extend %fill;

    &__padding {
        @extend %fill;
        border-style: solid;
        border-color: $box-model-padding;
    }

    &__margin {
        position: absolute;
        border-color: $box-model-margin;
    }

    &__popover {
        @extend %fill;
        left: 10px;
        right: auto;
    }
}
</style>
